import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { plyxApi } from "@/lib/plyxApi";

const PlyxReset = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Minimum 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    if (!token) { toast.error("Reset link is invalid"); return; }
    setLoading(true);
    try {
      await plyxApi.post("/plyx/auth/reset", { token, new_password: password });
      toast.success("Password updated. Welcome back 👋");
      navigate("/plyx/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed. Please request a new link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <h1 className="font-display text-4xl mb-2">Set a new password</h1>
        <p className="text-white/60 mb-8">
          Choose something easy to remember but hard to guess.
        </p>

        {!token ? (
          <div className="p-5 rounded-md border border-red-400/30 bg-red-500/10" data-testid="plyx-reset-bad-link">
            <div className="font-semibold mb-1">Missing reset token</div>
            <p className="text-sm text-white/70">
              This link looks incomplete. Please request a new password reset.
            </p>
            <Link to="/plyx/forgot" className="mt-4 inline-block text-sm font-semibold text-[#FF2D7A] hover:underline">
              Request new link
            </Link>
          </div>
        ) : (
          <form onSubmit={handle} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">New password</label>
              <input
                type="password" autoComplete="new-password" required minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="plyx-reset-password"
                className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Confirm password</label>
              <input
                type="password" autoComplete="new-password" required minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="plyx-reset-confirm"
                className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15"
              />
            </div>
            <button
              type="submit" disabled={loading}
              data-testid="plyx-reset-submit"
              className="w-full py-3 rounded-md text-[#0A0A0A] font-bold flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin"/>}
              Update password
            </button>
            <div className="text-center text-sm text-white/50 pt-2">
              <Link to="/plyx/login" className="hover:text-white">Back to login</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default PlyxReset;
